import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { teamAPI, analyticsAPI } from "../services/api";
import { getLogoSrc } from "../utils/logoHelper";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ScrollHint from "../components/ScrollHint";
import { announcementAPI } from "../services/announcementAPI";
import "./Home.css";

const API_BASE_URL = api.defaults.baseURL.replace("/api", "");

// ── Player stat snippet helpers ───────────────────────────
function calcAVG(games) {
  let h = 0,
    ab = 0;
  for (const g of games) {
    const b = g.batting;
    if (!b) continue;
    h += Number(b.h || 0);
    ab += Number(b.ab || 0);
  }
  if (ab < 10) return null; // min 10 AB for meaningful AVG
  return h / ab;
}

function calcERA(games) {
  let ipOuts = 0,
    er = 0;
  for (const g of games) {
    const p = g.pitching;
    if (!p) continue;
    const ipVal = parseFloat(p.ip || 0);
    const full = Math.floor(ipVal);
    const outs = Math.round((ipVal - full) * 10);
    ipOuts += full * 3 + outs;
    er += Number(p.er || 0);
  }
  if (ipOuts === 0) return null;
  const ipDecimal = Math.floor(ipOuts / 3) + (ipOuts % 3) / 3;
  if (ipDecimal < 3) return null; // min 3 IP for meaningful ERA
  return (er / ipDecimal) * 9;
}

function computeStatSnippet(player, games) {
  if (!games || games.length === 0) return { label: "No games yet", showModal: false, gameCount: 0 };
  const avg = calcAVG(games);
  const era = calcERA(games);
  const gameCount = games.length;
  // Threshold met — show stat, no modal
  if (avg != null && era != null) {
    if (era <= 2.0) return { label: `ERA ${era.toFixed(2)}`, showModal: false, gameCount };
    if (era <= 3.5 && avg < 0.28) return { label: `ERA ${era.toFixed(2)}`, showModal: false, gameCount };
    return { label: `AVG ${avg.toFixed(3).replace(/^0/, "")}`, showModal: false, gameCount };
  }
  if (era != null) return { label: `ERA ${era.toFixed(2)}`, showModal: false, gameCount };
  if (avg != null) return { label: `AVG ${avg.toFixed(3).replace(/^0/, "")}`, showModal: false, gameCount };
  // Below threshold — show game count with modal
  const gameWord = gameCount === 1 ? 'game' : 'games';
  return { label: `${gameCount} ${gameWord} tracked`, showModal: true, gameCount };
}

const GREETINGS = [
  (name) => `Hey, ${name}.`,
  (name) => `Good to see you, ${name}.`,
  (name) => `Ready to track one, ${name}?`,
  (name) => `The stands are calling, ${name}.`,
  (name) => `Great players have great parents, ${name}.`,
  (name) => `Another game, another story, ${name}.`,
  (name) => `In the words of Joe Maddon, ${name}, "Try not to suck."`,
  (name) =>
    `In the words of Yogi Berra, ${name}, "Baseball is 90% mental. The other half is physical."`,
  (name) =>
    `In the words of Yogi Berra, ${name}, "You can observe a lot just by watching."`,
  (name) =>
    `In the words of Willie Keeler, ${name}, "Hit 'em where they ain't."`,
];

function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamLimitInfo, setTeamLimitInfo] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerStatsMap, setPlayerStatsMap] = useState({});
  const [insightsModal, setInsightsModal] = useState(null); // { playerId, playerName, gameCount, dontShowAgain }
  const [announcements, setAnnouncements] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  // After players load, fetch game stats for each visible player in parallel
  useEffect(() => {
    if (!players.length || !teamLimitInfo) return;
    const visible = players;
    Promise.all(visible.map((p) => analyticsAPI.getPlayerGames(p.id)))
      .then((results) => {
        const map = {};
        results.forEach((res, i) => {
          map[visible[i].id] = computeStatSnippet(visible[i], res.data);
        });
        setPlayerStatsMap(map);
      })
      .catch(() => {});
  }, [players, teamLimitInfo]);

  const fetchData = async () => {
    try {
      const [
        teamsResponse,
        limitResponse,
        announcementsResponse,
        playersResponse,
      ] = await Promise.all([
        teamAPI.getAll(),
        teamAPI.getLimitInfo(),
        announcementAPI.getActive(),
        analyticsAPI.getPlayers(),
      ]);

      // Handle new response format (object with teams array + flags)
      const teamsData = teamsResponse.data.teams || teamsResponse.data;
      const hasLockedOrgTeams = teamsResponse.data.hasLockedOrgTeams || false;
      const lockedOrgTeamsCount = teamsResponse.data.lockedOrgTeamsCount || 0;

      setTeams(teamsData);
      setTeamLimitInfo({
        ...limitResponse.data,
        hasLockedOrgTeams,
        lockedOrgTeamsCount,
      });
      setAnnouncements(announcementsResponse.data);
      setPlayers(playersResponse.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has locked org teams (deleted org teams within 3 days due to subscription loss)
  const getLockedOrgTeamsInfo = () => {
    // Only show banner if user is NOT currently on organization tier
    // (If they're on org tier, deleted teams are just normal deletions, not subscription-related locks)
    if (teamLimitInfo?.subscriptionPlan === "organization") {
      return null;
    }

    // For org admins: check teams array for deleted org teams THAT THEY OWN
    const lockedOrgTeams = teams.filter(
      (t) =>
        t.organizationTeam === true &&
        t.deletedAt !== null &&
        t.userId === user?.id,
    );

    // For coaches: check the flag from backend OR if they have locked teams they don't own
    const lockedCoachTeams = teams.filter(
      (t) =>
        t.organizationTeam === true &&
        t.deletedAt !== null &&
        t.userId !== user?.id,
    );

    const hasLockedAsCoach =
      teamLimitInfo?.hasLockedOrgTeams || lockedCoachTeams.length > 0;

    if (lockedOrgTeams.length === 0 && !hasLockedAsCoach) return null;

    // If coach with locked teams
    if (hasLockedAsCoach && lockedOrgTeams.length === 0) {
      return {
        count: teamLimitInfo.lockedOrgTeamsCount,
        daysRemaining: 3, // Default to 3 days for coaches (we don't have exact time)
        isCoach: true,
      };
    }

    // Org admin with locked teams (has deleted teams in array)
    const firstTeam = lockedOrgTeams[0];
    const deletedDate = new Date(firstTeam.deletedAt);
    const now = new Date();
    const hoursElapsed = (now - deletedDate) / (1000 * 60 * 60);
    const hoursRemaining = Math.max(0, 72 - hoursElapsed);
    const daysRemaining = Math.floor(hoursRemaining / 24);

    // Window has fully passed — hide the banner
    if (hoursRemaining === 0) return null;

    return {
      count: lockedOrgTeams.length,
      daysRemaining,
      isCoach: false,
      lastDay: daysRemaining === 0,
    };
  };

  const handleCarouselScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const cardWidth = carouselRef.current.offsetWidth * 0.85; // 85% width cards
      const index = Math.round(scrollLeft / cardWidth);
      setActiveSlide(index);
    }
  };

  const scrollToSlide = (index) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.offsetWidth * 0.85;
      carouselRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
    }
  };

  // Helper function to split accessible vs locked items
  const getAccessibleItems = (items, limit) => {
    if (!items || items.length === 0)
      return { accessible: [], locked: [], lockedOrgTeams: [] };

    // Separate org teams from personal teams
    const orgTeams = items.filter((t) => t.organizationTeam === true);
    const personalTeams = items.filter((t) => !t.organizationTeam);

    // Find locked org teams (org teams with deletedAt set - recently deleted, shown for 3 days)
    const lockedOrgTeams = orgTeams.filter((t) => t.deletedAt !== null);
    const activeOrgTeams = orgTeams.filter((t) => t.deletedAt === null);

    // Sort personal teams by creation date (oldest first)
    const sortedPersonal = [...personalTeams].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );

    // Apply limit only to personal teams
    const accessiblePersonal = sortedPersonal.slice(0, limit);
    const lockedPersonal = sortedPersonal.slice(limit);

    return {
      accessible: [...activeOrgTeams, ...accessiblePersonal], // Active org teams + accessible personal
      locked: lockedPersonal, // Personal teams locked due to tier limits
      lockedOrgTeams, // Org teams locked due to subscription loss (have deletedAt)
    };
  };

  const canCreateTeam = () => {
    if (!teamLimitInfo) return false;
    return teamLimitInfo.canCreateMore;
  };

  if (loading) {
    return (
      <div className="home-page">
        <Navbar />
        <div className="home-container">
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="home-page">
        <Navbar />
        <div className="home-container">
          {/* Hero Section */}
          <section className="hero-section hero-compact">
            <div className="home-section-inner">
              <h2 className="hero-title-small">
                {user?.firstName
                  ? GREETINGS[Math.floor(Math.random() * GREETINGS.length)](
                      user.firstName,
                    )
                  : "Hey there."}
              </h2>
            </div>
          </section>

          {/* Org Subscription Expired Banner - now lives inside navy players section */}

          {/* Organization Tools Banner - Only for Org Tier */}
          {teamLimitInfo?.subscriptionPlan === "organization" && (
            <section className="org-tools-banner">
              <div className="org-banner-content">
                <span className="org-icon">🏢</span>
                <h3>Organization Admin Tools</h3>
              </div>
              <div className="org-tools-buttons">
                <button
                  className="btn-org-tool"
                  onClick={() => navigate("/organization/settings")}
                >
                  ⚙️ Settings
                </button>
                <button
                  className="btn-org-tool"
                  onClick={() => navigate("/organization/bulk-import")}
                >
                  📊 Create/Import Org Teams
                </button>
                <button
                  className="btn-org-tool"
                  onClick={() => navigate("/organization/manage-teams")}
                >
                  👥 Manage Org Teams
                </button>
              </div>
            </section>
          )}

          {/* Players Section */}
          <div className="home-section-navy">
            <div className="home-section-inner">
              {/* Org Subscription Expired Banner */}
              {(() => {
                const lockedOrgInfo = getLockedOrgTeamsInfo();
                if (!lockedOrgInfo) return null;

                // Banner for Org Admin
                if (!lockedOrgInfo.isCoach) {
                  return (
                    <div className="org-expired-banner">
                      <div className="banner-content">
                        <div className="banner-icon">🔒</div>
                        <div className="banner-text">
                          <strong>Organization Subscription Expired</strong>
                          <p>
                            {lockedOrgInfo.count} organization team
                            {lockedOrgInfo.count !== 1 ? "s are" : " is"}{" "}
                            locked.{" "}
                            {lockedOrgInfo.lastDay
                              ? "Last day to renew — restore access before midnight."
                              : `Renew within ${lockedOrgInfo.daysRemaining} day${lockedOrgInfo.daysRemaining !== 1 ? "s" : ""} to restore access.`}
                          </p>
                        </div>
                      </div>
                      <button
                        className="btn-renew-banner"
                        onClick={() => navigate("/membership")}
                      >
                        Renew Subscription
                      </button>
                    </div>
                  );
                }

                // Banner for Coaches
                return (
                  <div className="org-expired-banner-coach">
                    <div className="banner-content">
                      <div className="banner-text">
                        <strong>Organization Teams Unavailable</strong>
                        <p>
                          Your organization teams are currently unavailable due
                          to subscription expiration. Contact your administrator
                          to renew. ({lockedOrgInfo.daysRemaining} day
                          {lockedOrgInfo.daysRemaining !== 1 ? "s" : ""}{" "}
                          remaining)
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {(user?.role === "parent" || user?.role === "both") &&
                players.length === 0 && (
                  <section className="analytics-home-section">
                    <div
                      className="analytics-home-card"
                      onClick={() => navigate("/analytics/players/new")}
                    >
                      <div className="analytics-home-left">
                        <div className="analytics-home-label">ANALYTICS</div>
                        <div className="analytics-home-title">
                          Track your player's stats
                        </div>
                        <div className="analytics-home-sub">
                          Create a player profile to get started
                        </div>
                      </div>
                      <div className="analytics-home-arrow">→</div>
                    </div>
                  </section>
                )}
              {(user?.role === "parent" || user?.role === "both") &&
                players.length > 0 && (
                  <section className="home-players-section">
                    {/* Add Player button (Pro/Org only above section) */}
                    {teamLimitInfo?.subscriptionPlan !== "free" && (
                      <div
                        style={{ textAlign: "center", marginBottom: "24px" }}
                      >
                        <button
                          className="btn btn-success btn-lg"
                          onClick={() => navigate("/analytics/players/new")}
                        >
                          + Add Another Player
                        </button>
                      </div>
                    )}

                    <div className="section-header-home">
                      <h2 className="section-title-home">
                        Your Players ({players.length})
                        <div className="section-text">
                          Click a player to view their stats dashboard.
                        </div>
                      </h2>
                    </div>

                    <div className="teams-carousel-wrapper">
                      <ScrollHint />
                      <div className="teams-carousel">
                        {(() => {
                          return players
                            .map((player) => (
                              <div
                                key={player.id}
                                className="player-card-home"
                                onClick={() =>
                                  navigate(
                                    `/analytics/players/${player.id}/dashboard`,
                                  )
                                }
                              >
                                <div className="player-card-top">
                                  <div className="player-card-icon">
                                    {player.sport === "softball" ? "🥎" : "⚾"}
                                  </div>
                                  <h3 className="player-card-name">
                                    {player.firstName} {player.lastName}
                                  </h3>
                                  <p className="player-card-sub">
                                    {player.sport.charAt(0).toUpperCase() +
                                      player.sport.slice(1)}
                                    {player.positions?.length > 0
                                      ? ` · ${player.positions.join(" · ")}`
                                      : ""}
                                  </p>
                                  <p
                                    className="player-card-stat"
                                    style={playerStatsMap[player.id]?.showModal && !localStorage.getItem(`insights_dismissed_${player.id}`) ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '3px' } : {}}
                                    onClick={(e) => {
                                      const entry = playerStatsMap[player.id];
                                      if (entry?.showModal && !localStorage.getItem(`insights_dismissed_${player.id}`)) {
                                        e.stopPropagation();
                                        setInsightsModal({ playerId: player.id, playerName: `${player.firstName} ${player.lastName}`, gameCount: entry.gameCount, dontShowAgain: false });
                                      }
                                    }}
                                  >
                                    {playerStatsMap[player.id]?.label ?? "—"}
                                  </p>
                                </div>
                                <div className="player-card-actions">
                                  <button
                                    className="player-card-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/analytics/players/${player.id}/dashboard`,
                                      );
                                    }}
                                  >
                                    Stats →
                                  </button>
                                  <button
                                    className="player-card-btn player-card-btn-gold"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/analytics/players/${player.id}/brag-cards`,
                                      );
                                    }}
                                  >
                                    Brag Cards →
                                  </button>
                                </div>
                              </div>
                            ));
                        })()}
                      </div>
                    </div>

                    {/* Upgrade nudge for free users */}
                    {teamLimitInfo?.subscriptionPlan === "free" && (
                      <div style={{ textAlign: "center", marginTop: "24px" }}>
                        <p
                          style={{
                            color: "#718096",
                            marginBottom: "12px",
                            fontSize: "15px",
                          }}
                        >
                          Unlock AI Coach and analytics benchmarks — upgrade to Pro.
                        </p>
                        <button
                          className="btn btn-upgrade btn-lg"
                          onClick={() => navigate("/membership")}
                        >
                          ⭐ Upgrade to Pro
                        </button>
                      </div>
                    )}
                  </section>
                )}
            </div>
          </div>

          {announcements.length > 0 && (
            <div className="announcement-banner">
              <div className="announcement-text">
                {announcements[0].message}
              </div>
              {announcements[0].linkUrl && announcements[0].linkText && (
                <a
                  href={announcements[0].linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-announcement-link"
                >
                  {announcements[0].linkText}
                </a>
              )}
            </div>
          )}

          {/* Benefits Section - Only for Free Users */}
          {teamLimitInfo?.subscriptionPlan === "free" && (
            <section className="benefits-section">
              <div className="home-section-inner">
                <h2 className="benefits-title">
                  What You Get{" "}
                  <div className="section-text">
                    Why is this better? Here's a few reasons why!
                  </div>
                </h2>
                <div className="benefits-carousel-wrapper">
                  <ScrollHint />
                  <div
                    className="benefits-carousel"
                    ref={carouselRef}
                    onScroll={handleCarouselScroll}
                  >
                    <div className="benefit-item">
                      <h3>Know if they're actually getting better</h3>
                      <p>
                        Real stats from real games you tracked yourself. No
                        guessing.
                      </p>
                    </div>

                    <div className="benefit-item">
                      <h3>Track live from the stands</h3>
                      <p>
                        Every at-bat, pitch, and play logged in real time on any
                        device.
                      </p>
                    </div>

                    <div className="benefit-item">
                      <h3>AI Coach in your pocket</h3>
                      <p>
                        Personalized drills and development advice based on your
                        player's actual numbers.
                      </p>
                    </div>

                    <div className="benefit-item">
                      <h3>Turn big games into BragCards</h3>
                      <p>
                        One great outing becomes a shareable graphic in seconds.
                      </p>
                    </div>
                  </div>

                  {/* Pagination Dots */}
                  <div className="carousel-dots">
                    {[0, 1, 2, 3].map((index) => (
                      <button
                        key={index}
                        className={`carousel-dot ${
                          activeSlide === index ? "active" : ""
                        }`}
                        onClick={() => scrollToSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Upgrade Banner - Free Users Only */}
          {teamLimitInfo?.subscriptionPlan === "free" && (
            <div className="home-section-dark" style={{ padding: "24px 0" }}>
              <div className="home-section-inner">
                <div className="upgrade-banner">
                  <div className="banner-content">
                    <div className="banner-icon">⭐</div>
                    <div className="banner-text">
                      <strong>Upgrade to Pro</strong> for benchmarks, trend
                      analysis, AI Coach, and better graphics tools
                    </div>
                  </div>
                  <button
                    className="btn-banner-upgrade"
                    onClick={() => navigate("/membership")}
                  >
                    Upgrade to Pro
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Teams Section */}
          <div className="home-section-navy">
            <div className="home-section-inner">
              {(user?.role === "coach" ||
                user?.role === "both" ||
                user?.role === "unset") && (
                <section className="home-teams-section">
                  {(() => {
                    const limit = teamLimitInfo?.teamLimit || Infinity;
                    const { accessible, locked } = getAccessibleItems(
                      teams,
                      limit,
                    );
                    const hasAnyAccessibleTeams =
                      accessible.length > 0 || locked.length > 0;

                    return (
                      hasAnyAccessibleTeams &&
                      canCreateTeam() && (
                        <div
                          style={{ textAlign: "center", marginBottom: "24px" }}
                        >
                          <button
                            className="btn btn-success btn-lg"
                            onClick={() => navigate("/create-team")}
                          >
                            {(() => {
                              // Check if user only has org teams (assigned as coach)
                              const hasPersonalTeams = teams.some(
                                (t) => !t.organizationTeam,
                              );
                              const hasOrgTeams = teams.some(
                                (t) => t.organizationTeam,
                              );

                              // If user is org admin OR only has org teams (assigned coach)
                              if (
                                teamLimitInfo?.subscriptionPlan ===
                                  "organization" ||
                                (!hasPersonalTeams && hasOrgTeams)
                              ) {
                                return "+ Create Personal/Non-Org Team";
                              }
                              return "+ Create Another Team";
                            })()}
                          </button>
                        </div>
                      )
                    );
                  })()}
                  <div className="section-header-home">
                    <h2 className="section-title-home" id="teams-section">
                      Your Teams{" "}
                      {(() => {
                        if (teams.length === 0) return "";

                        // Use same logic as carousel to count only accessible teams
                        const limit = teamLimitInfo?.teamLimit || Infinity;
                        const { accessible } = getAccessibleItems(teams, limit);
                        return `(${accessible.length})`;
                      })()}
                      <div className="section-text">
                        Click your team to go to the team portal.
                      </div>
                    </h2>
                  </div>

                  {/* Info Banner for Locked Teams */}
                  {(() => {
                    const limit = teamLimitInfo?.teamLimit || Infinity;

                    // Only count ACTIVE PERSONAL teams (exclude org teams and deleted teams)
                    const personalTeams = teams.filter(
                      (t) => !t.organizationTeam && t.deletedAt === null,
                    );
                    const hasLockedPersonalTeams = personalTeams.length > limit;

                    return hasLockedPersonalTeams ? (
                      <div className="info-banner">
                        <div className="info-icon">💡</div>
                        <div className="info-content">
                          <h4>Why are my teams locked?</h4>
                          <p>
                            Your subscription has expired or you downgraded your
                            plan and you're now on the{" "}
                            <strong>{teamLimitInfo?.subscriptionPlan}</strong>{" "}
                            plan, which allows {limit} team
                            {limit === 1 ? "" : "s"}, which defaults to the
                            first team{limit === 1 ? "" : "s"} you created. Your
                            other teams and their graphics are safely saved.
                            Upgrade to access all your teams again.
                          </p>
                        </div>
                        <button
                          className="btn-upgrade-banner"
                          onClick={() => navigate("/membership")}
                        >
                          ⭐ Upgrade
                        </button>
                      </div>
                    ) : null;
                  })()}

                  {(() => {
                    const limit = teamLimitInfo?.teamLimit || Infinity;
                    const { accessible, locked } = getAccessibleItems(
                      teams,
                      limit,
                    );
                    const hasAnyAccessibleTeams =
                      accessible.length > 0 || locked.length > 0;

                    return !hasAnyAccessibleTeams ? (
                      <div className="empty-state-home">
                        <div className="empty-icon">⚾🥎</div>
                        <h3>No teams yet!</h3>
                        <p>
                          Create your first team to start making professional
                          graphics
                        </p>
                        <button
                          className="btn btn-primary btn-lg"
                          onClick={() => navigate("/create-team")}
                        >
                          Create Your First Team
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="teams-carousel-wrapper">
                          <ScrollHint />
                          <div className="teams-carousel">
                            {(() => {
                              const limit =
                                teamLimitInfo?.teamLimit || Infinity;
                              const { accessible, locked } = getAccessibleItems(
                                teams,
                                limit,
                              );

                              return (
                                <>
                                  {/* Accessible Teams */}
                                  {accessible.map((team) => {
                                    // Use org defaults if team colors are NULL
                                    const primaryColor =
                                      team.primaryColor ||
                                      team.orgPrimaryColor ||
                                      "#003366";
                                    const secondaryColor =
                                      team.secondaryColor ||
                                      team.orgSecondaryColor ||
                                      "#FFFFFF";

                                    return (
                                      <div
                                        key={team.id}
                                        className="team-card-home"
                                        onClick={() =>
                                          navigate(`/team/${team.id}`)
                                        }
                                        style={{
                                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                        }}
                                      >
                                        <div className="team-card-top">
                                          {(() => {
                                            const hasLogo =
                                              team.logoUrl || team.orgLogoUrl;
                                            const isOrgTeam =
                                              team.organizationTeam === true;
                                            const isPremiumUser =
                                              teamLimitInfo?.subscriptionPlan !==
                                              "free";
                                            const shouldShowLogo =
                                              hasLogo &&
                                              (isPremiumUser || isOrgTeam);

                                            return shouldShowLogo ? (
                                              <div className="team-logo-circle">
                                                <img
                                                  src={getLogoSrc(
                                                    team.logoUrl ||
                                                      team.orgLogoUrl,
                                                    API_BASE_URL,
                                                  )}
                                                  alt={`${team.name} logo`}
                                                />
                                              </div>
                                            ) : (
                                              <div className="team-logo-placeholder">
                                                {team.sport === "Softball"
                                                  ? "🥎"
                                                  : "⚾"}
                                              </div>
                                            );
                                          })()}
                                          <h3 className="team-name">
                                            {team.name}
                                          </h3>
                                          {team.isAssignedCoach &&
                                            team.orgAcronym && (
                                              <div className="assigned-coach-badge">
                                                👤 Assigned Coach -{" "}
                                                {team.orgAcronym}
                                              </div>
                                            )}
                                          {!team.isAssignedCoach &&
                                            team.organizationTeam &&
                                            team.orgAcronym && (
                                              <div className="org-admin-badge">
                                                {team.orgAcronym}
                                              </div>
                                            )}
                                          <p className="team-details">
                                            {team.ageGroup} • {team.season}
                                          </p>
                                          <p className="team-details">
                                            {team.sport}
                                          </p>
                                        </div>
                                        <div className="team-card-arrow">→</div>
                                      </div>
                                    );
                                  })}

                                  {/* Locked Teams */}
                                  {locked.map((team) => (
                                    <div
                                      key={team.id}
                                      className="team-card-home team-card-locked"
                                      style={{
                                        background: `linear-gradient(135deg, ${team.primaryColor} 0%, ${team.secondaryColor} 100%)`,
                                      }}
                                    >
                                      <div className="team-card-top">
                                        {(team.logoUrl || team.orgLogoUrl) &&
                                        teamLimitInfo?.subscriptionPlan !==
                                          "free" ? (
                                          <div className="team-logo-circle">
                                            <img
                                              src={getLogoSrc(
                                                team.logoUrl || team.orgLogoUrl,
                                                API_BASE_URL,
                                              )}
                                              alt={`${team.name} logo`}
                                            />
                                          </div>
                                        ) : (
                                          <div className="team-logo-placeholder">
                                            {team.sport === "Softball"
                                              ? "🥎"
                                              : "⚾"}
                                          </div>
                                        )}
                                        <h3 className="team-name">
                                          {team.name}
                                        </h3>
                                        <p className="team-details">
                                          {team.ageGroup} • {team.season}
                                        </p>
                                      </div>
                                      <div className="team-card-arrow">→</div>

                                      {/* Lock Overlay */}
                                      <div className="lock-overlay">
                                        <div className="lock-content">
                                          <div className="lock-icon">🔒</div>
                                          <button
                                            className="btn-unlock"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate("/membership");
                                            }}
                                          >
                                            Upgrade to Unlock
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Show upgrade if at team limit (but not premium) */}
                        {!canCreateTeam() &&
                          teamLimitInfo?.subscriptionPlan !== "premium" &&
                          teamLimitInfo?.subscriptionPlan !==
                            "organization" && (
                            <div
                              style={{ textAlign: "center", marginTop: "24px" }}
                            >
                              <p
                                style={{
                                  color: "#718096",
                                  marginBottom: "16px",
                                  fontSize: "15px",
                                }}
                              >
                                You've reached your team limit
                              </p>
                              <button
                                className="btn btn-upgrade btn-lg"
                                onClick={() => navigate("/membership")}
                              >
                                ⭐ Upgrade for More Teams
                              </button>
                            </div>
                          )}
                      </>
                    );
                  })()}
                </section>
              )}
            </div>
          </div>
        </div>
      {insightsModal && (
        <div className="fld-modal-overlay" onClick={() => setInsightsModal(null)}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Stats In Progress</div>
            <div className="fld-modal-body">
              {insightsModal.playerName} has {insightsModal.gameCount} {insightsModal.gameCount === 1 ? 'game' : 'games'} tracked. Stats appear after 10 at-bats for batting average and 3 innings pitched for ERA. Keep tracking and the numbers will follow.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', margin: '12px 0 4px' }}>
              <input
                type="checkbox"
                id="dont-show-again"
                checked={insightsModal.dontShowAgain}
                onChange={(e) => setInsightsModal(prev => ({ ...prev, dontShowAgain: e.target.checked }))}
              />
              <label htmlFor="dont-show-again" style={{ fontSize: 14, color: '#4a5568', cursor: 'pointer' }}>
                Don't show again for {insightsModal.playerName}
              </label>
            </div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (insightsModal.dontShowAgain) {
                    localStorage.setItem(`insights_dismissed_${insightsModal.playerId}`, 'true');
                  }
                  setInsightsModal(null);
                  navigate(`/analytics/players/${insightsModal.playerId}/dashboard`);
                }}
              >
                Continue Tracking
              </button>
            </div>
          </div>
        </div>
      )}
        <Footer />
      </div>
    </>
  );
}

export default Home;
